import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Icon,
  Message,
  Segment,
  Dimmer,
  Loader,
  Table,
  Divider,
  Label,
  Grid
} from 'semantic-ui-react';
import {
  fetchPathDefinition,
  clearError
} from '../../slices/pathRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
/**
 * PathDefinitionDetail component for displaying the details of a path definition
 * @returns {JSX.Element} PathDefinitionDetail component
 */
const PathDefinitionDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name } = useParams();

  // Get path definition data from Redux
  const { currentPathDefinition, loading, error } = useSelector(
    (state) => state.user.pathRegistry
  );

  // Local state
  const [localError, setLocalError] = useState(null);

  // Fetch path definition data
  useEffect(() => {
    if (name) {
      dispatch(fetchPathDefinition(name));
    }
  }, [dispatch, name]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      setTimeout(() => {
        dispatch(clearError());
        setLocalError(null);
      }, 5000);
    }
  }, [dispatch, error]);

  // Handle back button click
  const handleBack = () => {
    navigate('/admin/path-registry');
  };

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="sitemap" />
            <Header.Content>
              Path Definition Details
              <Header.Subheader>Viewing details for path definition: {name}</Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Navigation buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon
              labelPosition="left"
              onClick={handleBack}
            >
              <Icon name="arrow left" />
              Back to Path Registry
            </Button>
            <div>
              <Button
                as={Link}
                to={`/admin/path-registry/${name}/edit`}
                color="yellow"
                icon
                labelPosition="left"
                style={{ marginRight: '0.5em' }}
              >
                <Icon name="edit" />
                Edit
              </Button>
              <Button
                as={Link}
                to="/admin/path-registry/test"
                color="teal"
                icon
                labelPosition="left"
              >
                <Icon name="lab" />
                Test
              </Button>
            </div>
          </div>

          {/* Error message */}
          {localError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{localError}</p>
            </Message>
          )}

          {/* Path definition details */}
          {loading ? (
            <Segment>
              <Dimmer active inverted>
                <Loader inverted>Loading Path Definition</Loader>
              </Dimmer>
              <div style={{ height: '200px' }}></div>
            </Segment>
          ) : !currentPathDefinition ? (
            <Message warning>
              <Message.Header>Path Definition Not Found</Message.Header>
              <p>
                The path definition "{name}" could not be found. It may have been deleted or renamed.
              </p>
            </Message>
          ) : (
            <div>
              {/* Basic Information */}
              <Segment>
                <Header as="h3" dividing>Basic Information</Header>
                <Grid columns={2}>
                  <Grid.Row>
                    <Grid.Column>
                      <Table definition>
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell width={4}>Name</Table.Cell>
                            <Table.Cell>{currentPathDefinition.name}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>UUID</Table.Cell>
                            <Table.Cell>{currentPathDefinition.uuid || 'N/A'}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Description</Table.Cell>
                            <Table.Cell>{currentPathDefinition.description || 'No description'}</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Status</Table.Cell>
                            <Table.Cell>
                              {currentPathDefinition.active ? (
                                <Label color="green">Active</Label>
                              ) : (
                                <Label color="red">Inactive</Label>
                              )}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Created At</Table.Cell>
                            <Table.Cell>
                              {currentPathDefinition.createdAt ?
                                new Date(currentPathDefinition.createdAt).toLocaleString() :
                                'N/A'}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Updated At</Table.Cell>
                            <Table.Cell>
                              {currentPathDefinition.updatedAt ?
                                new Date(currentPathDefinition.updatedAt).toLocaleString() :
                                'N/A'}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </Grid.Column>
                    <Grid.Column>
                      <Message info>
                        <Message.Header>About Path Definitions</Message.Header>
                        <p>
                          Path definitions define how S3 paths are resolved to folder and file locations.
                          They are used to standardize path handling across the application.
                        </p>
                        <p>
                          Each path definition has a unique name and UUID, and contains a pattern and parameters
                          that define how paths are matched and resolved.
                        </p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>

              {/* Pattern Information */}
              <Segment>
                <Header as="h3" dividing>Pattern Information</Header>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>Required Segments</Table.Cell>
                      <Table.Cell>
                        {currentPathDefinition.pattern && currentPathDefinition.pattern.requiredSegments ? (
                          <div>
                            {currentPathDefinition.pattern.requiredSegments.map((segment, index) => (
                              <Label key={index} style={{ margin: '0.2em' }}>
                                {segment}
                              </Label>
                            ))}
                          </div>
                        ) : (
                          <em>None</em>
                        )}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Folder Template</Table.Cell>
                      <Table.Cell>
                        <code style={{
                          backgroundColor: '#f8f8f8',
                          padding: '0.5em',
                          borderRadius: '3px',
                          display: 'block'
                        }}>
                          {currentPathDefinition.pattern && currentPathDefinition.pattern.folderTemplate ?
                            currentPathDefinition.pattern.folderTemplate :
                            'N/A'}
                        </code>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Request Type</Table.Cell>
                      <Table.Cell>
                        <Label>
                          {currentPathDefinition.pattern && currentPathDefinition.pattern.requestType ?
                            currentPathDefinition.pattern.requestType :
                            'FOLDER'}
                        </Label>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </Segment>

              {/* Parameters */}
              <Segment>
                <Header as="h3" dividing>Parameters</Header>
                {currentPathDefinition.params && currentPathDefinition.params.length > 0 ? (
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Position</Table.HeaderCell>
                        <Table.HeaderCell>Required</Table.HeaderCell>
                        <Table.HeaderCell>Critical</Table.HeaderCell>
                        <Table.HeaderCell>Default Value</Table.HeaderCell>
                        <Table.HeaderCell>Description</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {currentPathDefinition.params.map((param, index) => (
                        <Table.Row key={index}>
                          <Table.Cell>{param.name}</Table.Cell>
                          <Table.Cell>{param.position}</Table.Cell>
                          <Table.Cell>
                            {param.required ? (
                              <Icon name="check" color="green" />
                            ) : (
                              <Icon name="times" color="red" />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {param.isCritical ? (
                              <Icon name="check" color="green" />
                            ) : (
                              <Icon name="times" color="red" />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {param.defaultValue !== undefined && param.defaultValue !== '' ?
                              param.defaultValue :
                              <em>None</em>
                            }
                          </Table.Cell>
                          <Table.Cell>{param.description || 'No description'}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                ) : (
                  <Message warning>
                    <p>No parameters defined for this path definition.</p>
                  </Message>
                )}
              </Segment>

              {/* Example Usage */}
              <Segment>
                <Header as="h3" dividing>Example Usage</Header>
                <p>
                  Here's an example of how this path definition might be used:
                </p>

                {currentPathDefinition.pattern && currentPathDefinition.pattern.requiredSegments && (
                  <div>
                    <Header as="h4">Example Path</Header>
                    <code style={{
                      backgroundColor: '#f8f8f8',
                      padding: '0.5em',
                      borderRadius: '3px',
                      display: 'block'
                    }}>
                      {currentPathDefinition.pattern.requiredSegments.join('/')}/
                      {currentPathDefinition.params && currentPathDefinition.params
                        .filter(param => param.name !== 'fileName')
                        .map(param => `{${param.name}}`)
                        .join('/')}/
                      {currentPathDefinition.params && currentPathDefinition.params
                        .find(param => param.name === 'fileName') ? 'example.jpg' : ''}
                    </code>

                    <Header as="h4">Resolved S3 Key</Header>
                    <code style={{
                      backgroundColor: '#f8f8f8',
                      padding: '0.5em',
                      borderRadius: '3px',
                      display: 'block'
                    }}>
                      {currentPathDefinition.pattern.folderTemplate
                        .replace(/\{([^}]+)\}/g, (match, param) => `{${param}}`)}/
                      {currentPathDefinition.params && currentPathDefinition.params
                        .find(param => param.name === 'fileName') ? 'example.jpg' : ''}
                    </code>
                  </div>
                )}

                <Message info style={{ marginTop: '1em' }}>
                  <p>
                    To test this path definition with real values, use the
                    <Link to="/admin/path-registry/test"> Path Testing Tool</Link>.
                  </p>
                </Message>
              </Segment>
            </div>
          )}
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default PathDefinitionDetail;
